import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Button } from '@patternfly/react-core';
import Tl from 'localization/LocalizationKeys';
import SimpleTable from 'components/Common/SimpleTable/SimpleTable';
import ColorPicker from 'components/Common/ColorPicker/ColorPicker';
import { palette as patternflyPalette } from 'components/Admin/ThemeEditor/originals';
import useBoolean from 'utils/hooks/useBoolean';
import CssModal from './CssModal';
import { Components } from 'components/Model/Components';
import ModelType from 'components/Model/ModelType';

const getDefaultValue = (value) => {
  return _.isFunction(value) ? value() : value;
};

export const getPropStyle = (model) => {
  if (model.property.startsWith('style.')) {
    // remove style. from model.property value
    const style = model.property.replace('style', '');
    const lastDotIndex = style.lastIndexOf('.');
    const property = style.substring(lastDotIndex + 1);
    const selector = style.substring(0, style.lastIndexOf('.'));
    return { selector, property, value: model.value, defaultValue: getDefaultValue(model.defaultValue) };
  }
  return { property: model.property, value: model.value, defaultValue: getDefaultValue(model.defaultValue) };
};

const setAllProperties = (themeProperties) => {
  const root = document.documentElement;
  _.forEach(themeProperties, model => {
    const props = getPropStyle(model);
    if (props.selector) {
      const result = root.querySelector(props.selector);
      if (result) {
        result.style[_.camelCase(props.property)] = props.value;
      }
    } else {
      root.style.setProperty(props.property, props.value);
    }
  });
};


const generateCssOutput = (themeProperties) => {
  const indent = '  ';
  let colorLines = [];
  let lines = [];

  _.forEach(themeProperties, (model) => {
    const value = model.value ?? getDefaultValue(model.defaultValue);
    if (value == null || value === getDefaultValue(model.defaultValue)) {
      return;
    }
    const props = getPropStyle(model);

    if (props.selector) {
      lines.push(`${props.selector} {\n${indent}${indent}${props.property}: ${model.value} !important;\n}\n`);
    } else {
      colorLines.push(`${props.property}: ${model.value};`);
    }
  });
  return `* {\n${colorLines.join('\n')}\n}\n${lines.join('\n')}\n`;
};

const palette = _.map(patternflyPalette);

const ColorPickerComponent = ({ model, value, onChange }) => {
  return (<ColorPicker
    palette={palette}
    value={value}
    onChange={(newValue) => onChange(model, newValue)}
  />);
};

const ThemeComponents = {
  ...Components,
  [ModelType.COLOR]: ColorPickerComponent
};

const ReadOnlyComponents = {
  [ModelType.TEXT]: ({ value }) => {
    return (<div>{ value }</div>);
  },
  [ModelType.COLOR]: ({ value }) => {
    return (<Button
      style={{
        backgroundColor: value,
        height: '28px',
        width: '28px'
      }}
    />);
  }
};
const ThemeEditor = ({ themeProperties: themePropertiesIn }) => {

  const [isShowingCssModal, showCssModal, hideCssModal] = useBoolean(false);
  const [themeProperties, setThemeProperties] = useState(() => _.cloneDeep(themePropertiesIn));


  useEffect(() => {
    setAllProperties(themeProperties);
  }, [themeProperties]);

  const onChangeValue = useCallback((row, value) => {
    setThemeProperties(oldTheme => {
      const newTheme = [...oldTheme];
      _.find(newTheme, { property: row.property }).value = value;

      // Trigger an update before next iteration
      // This fixes the issue with defaultValue not being updated on next iteration
      setAllProperties(newTheme);

      return newTheme;
    });
  }, []);

  const columns = useMemo(() => [
    {
      label: Tl.common.name,
      path: 'locations[0].label',
      isSortable: true,
      searchItem: true
    },
    {
      label: Tl.common.value,
      path: 'value',
      render: (value, row) => {
        const Component = ThemeComponents[row.type];
        if (Component) {
          return (<div style={{ width: '200px' }}><Component
            model={row}
            value={value ?? getDefaultValue(row.defaultValue)}
            onChange={onChangeValue}
          /></div>);
        }
        return (<div/>);
      }
    },
    {
      label: 'Default value',
      path: 'defaultValue',
      render: (color, row) => {
        const Component = ReadOnlyComponents[row.type];
        if (Component) {
          return (<div style={{ width: '200px' }}><Component
            value={getDefaultValue(color)}
          /></div>);
        }
      }
    }
  ], [onChangeValue]);

  const actions = useMemo(() => [
    {
      label: 'Reset all',
      onClick: () => {
        setThemeProperties(oldTheme => {
          const newTheme = [...oldTheme];
          _.forEach(newTheme, model => {
            model.value = getDefaultValue(model.defaultValue);
          }, []);
          setAllProperties(newTheme);
          return newTheme;
        });
      }
    },
    {
      label: 'Show output',
      onClick: () => {
        showCssModal();
      }
    }
  ], [showCssModal]);

  const rowActions = useMemo(() => [
    {
      title: 'Reset',
      isOverflow: true,
      variant: 'primary',
      onClick: (row) => {
        setThemeProperties(oldTheme => {
          const newTheme = [...oldTheme];
          const prop = _.find(newTheme, { property: row.property });
          prop.value = getDefaultValue(prop.defaultValue);
          setAllProperties(newTheme);

          return newTheme;
        });
      }
    }
  ], []);

  const cssOutput = useMemo(() => generateCssOutput(themeProperties), [themeProperties]);

  return (<>
    <SimpleTable columns={columns} data={themeProperties} globalActions={actions} actions={rowActions}/>
    <CssModal isOpen={isShowingCssModal} onClose={hideCssModal} value={cssOutput}/>
  </>);
};

export default ThemeEditor;
